/* The one file that changes when the backend lands.
 *
 * Set VITE_API_BASE (e.g. https://rms-api-read-xxxx.run.app) and every call routes
 * to the real Cloud Run services. Leave it unset and the mock layer serves instead.
 *
 * Read paths hit rms-api-read; write paths hit rms-api-write (A2 — the split SA
 * boundary). Both carry the Firebase ID token and a correlation ID (C5).
 */

import { mockApi } from './mock'
import { idToken, googleGroupsToken } from '../auth'
import type {
  MyAccess, LiveAccess, CatalogueFolder, SearchResult, MigrationQueue, ReverseLookup, AccessPreflight,
  AccessRequestResult, Inbox, InboxItem, RequestOutcomeKind,
  OwnersResponse, RegisterProposal, RegisterMe, AdoptionReport, AdoptionPeriod, Leaderboard,
  MyGroups, GroupMember, GroupChange, MoveCheck, MoveResult,
  ProjectOptions, ProjectNameCheck, ProjectRequest, ProjectResult, FolderFilesPage,
} from '../types'

const READ_BASE = import.meta.env.VITE_API_BASE ?? ''
const WRITE_BASE = import.meta.env.VITE_API_WRITE_BASE ?? READ_BASE
const LIVE = READ_BASE !== ''

export const isLive = () => LIVE

/* T4: one Idempotency-Key per folder for the life of the page, so a double
   click or a retry after a network error lands on the same recorded request
   instead of creating a second one. A reload starts fresh. */
const requestKeys = new Map<string, string>()
function requestKey(folderId: string): string {
  let k = requestKeys.get(folderId)
  if (!k) { k = crypto.randomUUID(); requestKeys.set(folderId, k) }
  return k
}

function googleHeader(): Record<string, string> {
  const t = googleGroupsToken()
  return t ? { 'X-RMS-Google-Token': t } : {}
}

function correlationId(): string {
  return 'c-' + Math.random().toString(16).slice(2, 10)
}

/** Firebase ID token when Firebase Auth is configured; nothing otherwise (mock / IAP demo). */
async function authHeader(): Promise<Record<string, string>> {
  const token = await idToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function call<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const cid = correlationId()
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-Id': cid,
      ...(await authHeader()),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err = new Error(body.message ?? body.error ?? `Request failed (${res.status})`) as Error & { correlationId?: string; code?: string }
    err.correlationId = body.correlationId ?? cid
    err.code = body.error
    throw err
  }
  return res.json() as Promise<T>
}

export const api = {
  /* Adoption: one portal visit per person per EAT day, recorded server-side.
     Sends nothing about the page; the server derives who and which day. */
  presence: (): Promise<{ date: string; recorded: boolean } | null> =>
    LIVE ? call(READ_BASE, '/api/presence', { method: 'POST' }) : Promise.resolve(null),

  /* Adoption dashboard: project-team allowlist, enforced by the API. */
  getAdoptionMe: (): Promise<{ allowed: boolean }> =>
    LIVE ? call(READ_BASE, '/api/adoption/me') : Promise.resolve({ allowed: false }),
  getAdoption: (period: AdoptionPeriod): Promise<AdoptionReport> =>
    call(READ_BASE, `/api/adoption?period=${period}`),

  /* Migration leaderboard: department totals, every signed-in person. */
  getLeaderboard: (): Promise<Leaderboard> =>
    LIVE ? call(READ_BASE, '/api/leaderboard') : mockApi.getLeaderboard(),

  whoami: (): Promise<{ email: string } | null> =>
    LIVE ? call<{ email: string }>(READ_BASE, '/api/whoami') : mockApi.whoami(),

  getAccess: (): Promise<MyAccess> =>
    LIVE ? call(READ_BASE, '/api/me/access') : mockApi.getAccess(),

  refreshAccess: (): Promise<MyAccess> =>
    LIVE ? call(READ_BASE, '/api/me/access/refresh', { method: 'POST' }) : mockApi.getAccess(),

  /* Epic v4 A2: asked after My access renders; new folders slot in. Any failure is no new folders. */
  getAccessLive: (): Promise<LiveAccess | null> =>
    LIVE ? call<LiveAccess>(READ_BASE, '/api/me/access/live').catch(() => null) : mockApi.getAccessLive(),

  /* S3-1: the catalogue is served by the authenticated API, never from the Hosting
     CDN. Hosting is unauthenticated and this payload names every drive, folder,
     group address and owner. Do not move this to a static file.
     The route wraps the array as { folders, correlationId } (C5 — every
     response carries a correlation ID), so unwrap it here. */
  getCatalogue: (): Promise<CatalogueFolder[]> =>
    LIVE
      ? call<{ folders: CatalogueFolder[] }>(READ_BASE, '/api/catalogue').then((r) => r.folders)
      : mockApi.getCatalogue(),

  /* Same wrapping as getCatalogue — { results, correlationId }. */
  search: (q: string, filters: Record<string, string> = {}): Promise<SearchResult[]> => {
    if (!LIVE) return mockApi.search(q)
    const qs = new URLSearchParams({ q, ...filters })
    return call<{ results: SearchResult[] }>(READ_BASE, `/api/search?${qs}`).then((r) => r.results)
  },

  /* Folder files (3 Nov 2026): a folder's files, read as the signed-in person. Read service. */
  getFolderFilesEnabled: (): Promise<{ enabled: boolean }> =>
    LIVE ? call(READ_BASE, '/api/folder-files/enabled') : mockApi.getFolderFilesEnabled(),
  getFolderFiles: (folderId: string, pageToken?: string): Promise<FolderFilesPage> =>
    LIVE
      ? call<FolderFilesPage>(READ_BASE, `/api/folder-files/${encodeURIComponent(folderId)}${pageToken ? `?pageToken=${encodeURIComponent(pageToken)}` : ''}`)
      : mockApi.getFolderFiles(folderId, pageToken),
  getReverse: (folderId: string): Promise<ReverseLookup | null> =>
    LIVE ? call(READ_BASE, `/api/reverse/${folderId}`) : mockApi.getReverse(folderId),

  /* The whole /api/migration router is mounted on the write service only. */
  getMigration: (): Promise<MigrationQueue> =>
    LIVE ? call(WRITE_BASE, '/api/migration/queue') : mockApi.getMigration(),

  /* POST /api/requests is a write-service route (index.ts mounts it inside
     API_MODE === 'write') — was hitting READ_BASE, harmless in this demo's
     single-origin deploy but a real 404 against the real split services. */
  requestAccess: (folderId: string, reason: string) =>
    LIVE
      ? call<AccessRequestResult>(WRITE_BASE, '/api/requests', {
          method: 'POST', body: JSON.stringify({ folderId, reason }),
          headers: { 'Idempotency-Key': requestKey(folderId) },
        })
      : mockApi.requestAccess(folderId, reason),

  /* Owners page: catalogue + effective owner from the register (read service). */
  getOwners: (): Promise<OwnersResponse> =>
    LIVE ? call<OwnersResponse>(READ_BASE, '/api/owners') : mockApi.getOwners(),

  /* Owner register (write service). */
  getRegisterMe: (): Promise<RegisterMe> =>
    LIVE ? call<RegisterMe>(WRITE_BASE, '/api/register/me') : mockApi.getRegisterMe(),
  getProposals: (): Promise<{ isApprover: boolean; items: RegisterProposal[] }> =>
    LIVE ? call(WRITE_BASE, '/api/register/proposals') : mockApi.getProposals(),
  proposeOwner: (folderId: string, ownerEmail: string, deputyEmail: string, reason: string): Promise<{ proposal: RegisterProposal }> =>
    LIVE
      ? call(WRITE_BASE, '/api/register/proposals', { method: 'POST', body: JSON.stringify({ folderId, ownerEmail, deputyEmail, reason }) })
      : mockApi.proposeOwner(folderId, ownerEmail, deputyEmail, reason),
  decideProposal: (proposalId: string, decision: 'approve' | 'reject', note: string): Promise<{ proposal: RegisterProposal }> =>
    LIVE
      ? call(WRITE_BASE, `/api/register/proposals/${encodeURIComponent(proposalId)}/decide`, { method: 'POST', body: JSON.stringify({ decision, note }) })
      : mockApi.decideProposal(proposalId, decision, note),

  /* My groups — write service. Member reads and changes carry the owner's own Google token. */
  getMyGroups: (): Promise<MyGroups> =>
    LIVE ? call<MyGroups>(WRITE_BASE, '/api/groups/mine') : mockApi.getMyGroups(),
  getGroupMembers: (group: string): Promise<{ members: GroupMember[] }> =>
    LIVE
      ? call(WRITE_BASE, `/api/groups/${encodeURIComponent(group)}/members`, { headers: googleHeader() })
      : mockApi.getGroupMembers(group),
  changeGroupMember: (group: string, action: 'add' | 'remove', email: string, reason: string, key: string, role: 'MEMBER' | 'OWNER' = 'MEMBER'): Promise<{ change: GroupChange }> =>
    LIVE
      ? call(WRITE_BASE, `/api/groups/${encodeURIComponent(group)}/members${action === 'remove' ? '/remove' : ''}`, {
          method: 'POST', headers: { ...googleHeader(), 'Idempotency-Key': key }, body: JSON.stringify({ email, reason, role }),
        })
      : mockApi.changeGroupMember(group, action, email, reason, role),
  getGroupChanges: (group: string): Promise<{ changes: GroupChange[] }> =>
    LIVE ? call(WRITE_BASE, `/api/groups/${encodeURIComponent(group)}/changes`) : mockApi.getGroupChanges(group),

  /* T4b owner inbox — same write service as requests. */
  getInbox: (): Promise<Inbox> =>
    LIVE ? call<Inbox>(WRITE_BASE, '/api/requests/inbox') : mockApi.getInbox(),

  resolveRequest: (requestId: string, outcome: RequestOutcomeKind, note: string): Promise<{ item: InboxItem }> =>
    LIVE
      ? call<{ item: InboxItem }>(WRITE_BASE, `/api/requests/${encodeURIComponent(requestId)}/resolve`, {
          method: 'POST', body: JSON.stringify({ outcome, note }),
        })
      : mockApi.resolveRequest(requestId, outcome, note),

  /* Write paths — rms-api-write, guarded by assertCanWrite() and the kill switch.
     Moves a file the user picked from their own Drive (My Drive or a shared
     drive, via the Google Picker in drivePicker.ts) into the resolved
     folder — not a local desktop upload. */
  file: (folderId: string, idempotencyKey: string, driveFileId: string, recordSeries?: string, entity?: string) =>
    LIVE
      ? call<{ ok: boolean; fileId: string }>(WRITE_BASE, '/api/file', {
          method: 'POST',
          body: JSON.stringify({ folderId, driveFileId, recordSeries, entity }),
          headers: { 'Idempotency-Key': idempotencyKey },
        })
      : mockApi.file(folderId, idempotencyKey),

  /* R1 (adoption addendum) — GET /api/me/access/preflight/:folderId. Lives on
     the WRITE service, not READ: it reuses assertCanWrite's live, uncached
     probe (canAddChildren) rather than MyAccess's `canEdit`-derived field,
     which is served from a cache allowed to be 45-90 minutes stale
     (decisions A1/A3). A near-identical READ/WRITE_BASE mixup already
     shipped once this session (requestAccess, fixed in 6f8077d) — this call
     must stay on WRITE_BASE. */
  getPreflight: (folderId: string): Promise<AccessPreflight> =>
    LIVE
      ? call<AccessPreflight>(WRITE_BASE, `/api/me/access/preflight/${folderId}`)
      : mockApi.getPreflight(folderId),

  /* Was always hitting WRITE_BASE regardless of LIVE, same shape of bug as
     requestAccess's earlier READ/WRITE_BASE mixup — harmless once deployed
     since LIVE is always true there, but broken in local mock-mode dev. */
  /* Move folder for me: write service; every check re-runs live as the signed-in person. */
  getMoveEnabled: (): Promise<{ enabled: boolean }> =>
    LIVE ? call(WRITE_BASE, '/api/move-folder/enabled') : mockApi.getMoveEnabled(),
  checkMove: (source: string, destinationFolderId?: string): Promise<MoveCheck> =>
    LIVE
      ? call<MoveCheck>(WRITE_BASE, '/api/move-folder/check', { method: 'POST', body: JSON.stringify({ source, destinationFolderId }) })
      : mockApi.checkMove(source, destinationFolderId),
  moveFolder: (source: string, destinationFolderId: string): Promise<MoveResult> =>
    LIVE
      ? call<MoveResult>(WRITE_BASE, '/api/move-folder', { method: 'POST', body: JSON.stringify({ source, destinationFolderId }) })
      : mockApi.moveFolder(source, destinationFolderId),

  /* Start a project: write service; rms-drive-admin@ builds the folder and team group. */
  getProjectsEnabled: (): Promise<{ enabled: boolean }> =>
    LIVE ? call(WRITE_BASE, '/api/projects/enabled') : mockApi.getProjectsEnabled(),
  getProjectOptions: (): Promise<ProjectOptions> =>
    LIVE ? call<ProjectOptions>(WRITE_BASE, '/api/projects/options') : mockApi.getProjectOptions(),
  checkProjectName: (name: string): Promise<ProjectNameCheck> =>
    LIVE
      ? call<ProjectNameCheck>(WRITE_BASE, '/api/projects/check', { method: 'POST', body: JSON.stringify({ name }) })
      : mockApi.checkProjectName(name),
  startProject: (req: ProjectRequest): Promise<ProjectResult> =>
    LIVE
      ? call<ProjectResult>(WRITE_BASE, '/api/projects', { method: 'POST', body: JSON.stringify(req) })
      : mockApi.startProject(req),

  move: (itemIds: string[]) =>
    LIVE
      ? call<{ moved: string[]; failed: string[] }>(WRITE_BASE, '/api/migration/move', {
          method: 'POST', body: JSON.stringify({ itemIds }),
        })
      : mockApi.move(itemIds),
}
